import type { ComponentType, SVGProps } from "react";

import AdminIcon from "@/assets/icons/admin.svg?react";
import AlertTriangleIcon from "@/assets/icons/alert-triangle.svg?react";
import AlignCenterIcon from "@/assets/icons/align-center.svg?react";
import AngleDoubleLeftIcon from "@/assets/icons/angle-double-left.svg?react";
import AngleDoubleRightIcon from "@/assets/icons/angle-double-right.svg?react";
import AngleDownIcon from "@/assets/icons/angle-down.svg?react";
import AngleLeftIcon from "@/assets/icons/angle-left.svg?react";
import AngleRightBIcon from "@/assets/icons/angle-right-b.svg?react";
import AngleRightIcon from "@/assets/icons/angle-right.svg?react";
import AngleUpIcon from "@/assets/icons/angle-up.svg?react";
import ArrowGrowthIcon from "@/assets/icons/arrow-growth.svg?react";
import BookIcon from "@/assets/icons/book.svg?react";
import CalendarIcon from "@/assets/icons/calendar.svg?react";
import CancelCircleIcon from "@/assets/icons/cancel-circle.svg?react";
import ChartNetworkIcon from "@/assets/icons/chart-network.svg?react";
import CheckCircleFilledIcon from "@/assets/icons/check-circle-filled.svg?react";
import CheckCircleIcon from "@/assets/icons/check-circle.svg?react";
import CheckIcon from "@/assets/icons/check.svg?react";
import CircleIcon from "@/assets/icons/circle.svg?react";
import CopyIcon from "@/assets/icons/copy.svg?react";
import CpuIcon from "@/assets/icons/cpu.svg?react";
import CubeIcon from "@/assets/icons/cube.svg?react";
import DiamondIcon from "@/assets/icons/diamond.svg?react";
import DollarAltIcon from "@/assets/icons/dollar-alt.svg?react";
import DownloadAltIcon from "@/assets/icons/download-alt.svg?react";
import EditIcon from "@/assets/icons/edit.svg?react";
import EyeIcon from "@/assets/icons/eye.svg?react";
import EyeSlashIcon from "@/assets/icons/eye-slash.svg?react";
import FilePenLineIcon from "@/assets/icons/file-pen-line.svg?react";
import FilterIcon from "@/assets/icons/filter.svg?react";
import FlashIcon from "@/assets/icons/flash.svg?react";
import FuelIcon from "@/assets/icons/fuel.svg?react";
import GridIcon from "@/assets/icons/grid.svg?react";
import GripVerticalIcon from "@/assets/icons/grip-vertical.svg?react";
import LinkIcon from "@/assets/icons/link.svg?react";
import LockIcon from "@/assets/icons/lock.svg?react";
import LogInIcon from "@/assets/icons/log-in.svg?react";
import LogOutIcon from "@/assets/icons/log-out.svg?react";
import LogoIcon from "@/assets/icons/logo.svg?react";
import MoreVerticalIcon from "@/assets/icons/more-vertical.svg?react";
import PauseIcon from "@/assets/icons/pause.svg?react";
import PlayIcon from "@/assets/icons/play.svg?react";
import PlusCircleIcon from "@/assets/icons/plus-circle.svg?react";
import PlusIcon from "@/assets/icons/plus.svg?react";
import ReferralIcon from "@/assets/icons/referral.svg?react";
import RefreshIcon from "@/assets/icons/refresh.svg?react";
import RobotIcon from "@/assets/icons/robot.svg?react";
import RoundedSquareIcon from "@/assets/icons/rounded-square.svg?react";
import SaveIcon from "@/assets/icons/save.svg?react";
import SearchIcon from "@/assets/icons/search.svg?react";
import SettingsIcon from "@/assets/icons/settings.svg?react";
import ShieldIcon from "@/assets/icons/shield.svg?react";
import SignalIcon from "@/assets/icons/signal.svg?react";
import SpinnerIcon from "@/assets/icons/spinner.svg?react";
import TrashIcon from "@/assets/icons/trash.svg?react";
import UsdtIcon from "@/assets/icons/usdt.svg?react";
import UserIcon from "@/assets/icons/user.svg?react";
import UsersIcon from "@/assets/icons/users.svg?react";
import XIcon from "@/assets/icons/x.svg?react";
import { cn } from "@/lib/cn";

type IconComponent = ComponentType<SVGProps<SVGSVGElement>>;

const ICONS = {
  admin: AdminIcon,
  "alert-triangle": AlertTriangleIcon,
  "align-center": AlignCenterIcon,
  "angle-double-left": AngleDoubleLeftIcon,
  "angle-double-right": AngleDoubleRightIcon,
  "angle-down": AngleDownIcon,
  "angle-left": AngleLeftIcon,
  "angle-right-b": AngleRightBIcon,
  "angle-right": AngleRightIcon,
  "angle-up": AngleUpIcon,
  "arrow-growth": ArrowGrowthIcon,
  book: BookIcon,
  calendar: CalendarIcon,
  "cancel-circle": CancelCircleIcon,
  "chart-network": ChartNetworkIcon,
  "check-circle-filled": CheckCircleFilledIcon,
  "check-circle": CheckCircleIcon,
  check: CheckIcon,
  circle: CircleIcon,
  copy: CopyIcon,
  cpu: CpuIcon,
  cube: CubeIcon,
  diamond: DiamondIcon,
  "dollar-alt": DollarAltIcon,
  "download-alt": DownloadAltIcon,
  edit: EditIcon,
  eye: EyeIcon,
  "eye-slash": EyeSlashIcon,
  "file-pen-line": FilePenLineIcon,
  filter: FilterIcon,
  flash: FlashIcon,
  fuel: FuelIcon,
  grid: GridIcon,
  "grip-vertical": GripVerticalIcon,
  link: LinkIcon,
  lock: LockIcon,
  "log-in": LogInIcon,
  "log-out": LogOutIcon,
  logo: LogoIcon,
  "more-vertical": MoreVerticalIcon,
  pause: PauseIcon,
  play: PlayIcon,
  "plus-circle": PlusCircleIcon,
  plus: PlusIcon,
  referral: ReferralIcon,
  refresh: RefreshIcon,
  robot: RobotIcon,
  "rounded-square": RoundedSquareIcon,
  save: SaveIcon,
  search: SearchIcon,
  settings: SettingsIcon,
  shield: ShieldIcon,
  signal: SignalIcon,
  spinner: SpinnerIcon,
  trash: TrashIcon,
  usdt: UsdtIcon,
  user: UserIcon,
  users: UsersIcon,
  x: XIcon,
} satisfies Record<string, IconComponent>;

export type IconName = keyof typeof ICONS;

interface IconProps {
  name: IconName;
  className?: string;
}

export const Icon = ({ name, className }: IconProps) => {
  const IconComponent = ICONS[name];

  return (
    <IconComponent
      aria-hidden="true"
      className={cn("size-5 shrink-0", className)}
      focusable="false"
    />
  );
};
